import { useTranslation } from 'react-i18next'
import { TransparentButton, ButtonType } from '../../common/Buttons/TransparentButton'
import { Dialog } from '../../common/Dialog/Dialog'
import './ManagerModal.scss'
import { useGetAllCustomersQuery } from '../../../redux/api/customersApi'
import { useEffect, useMemo, useState } from 'react'
import { Arr } from '../../../helpers/Arr'
import {
  isSupplierOrder,
  useUpdateOrderInternalMutation,
  useUpdateOrderSuppliersMutation,
} from '../../../redux/api/ordersApi'
import { printDate } from '../../../helpers/Utils'
import { ContactKind, ManagerKind } from '@ail/customers-api'
import { useAuth } from '../../../auth/UseAuth'
import {
  OrderInternalFormFieldViewModel,
  OrderInternalInputModel,
  OrderInternalViewModel,
  OrderSuppliersFormFieldViewModel,
  OrderSuppliersInputModel,
  OrderSuppliersViewModel,
  WorkflowOrderStepStatus,
} from '@ail/orders-api'

interface ManagerModalProps {
  data?: {
    order: OrderInternalViewModel | OrderSuppliersViewModel
    field: OrderInternalFormFieldViewModel | OrderSuppliersFormFieldViewModel
  }
  onClose: () => void
}

export function ManagerModal(props: ManagerModalProps) {
  const [selectedManager, setSelectedManager] = useState<string>('')
  const [t, i18n] = useTranslation()
  const { user } = useAuth()
  const allCustomers = useGetAllCustomersQuery(undefined, { skip: !user?.isInternalUser })
  const [updateOrderInternal] = useUpdateOrderInternalMutation({ fixedCacheKey: 'UpdateInternal' })
  const [updateOrderSuppliers] = useUpdateOrderSuppliersMutation({ fixedCacheKey: 'UpdateSuppliers' })

  const managers = useMemo(() => {
    const managerMap: Record<string, string> = {}
    if (allCustomers.data) {
      for (const item of allCustomers.data) {
        if (!item.customer) {
          continue
        }
        for (const manager of item.customer.managers) {
          if (
            (manager.kind === ManagerKind.OfficeSales && manager.active) ||
            manager.firstName?.toLowerCase() === 'team'
          ) {
            const managerEmail = manager.contacts.find((c) => c.kind === ContactKind.Email)
            if (managerEmail?.value && manager.displayName && !managerMap[managerEmail.value]) {
              managerMap[managerEmail.value] = manager.displayName
            }
          }
        }
      }
    }
    const filteredManagers = Object.keys(managerMap).map((k) => ({ email: k, displayName: managerMap[k] }))
    return Arr.sortBy(filteredManagers, 'displayName')
  }, [allCustomers.data])

  useEffect(() => {
    if (props.data) {
      setSelectedManager(props.data.field.value ?? '')
    }
  }, [props.data])

  const title = props.data?.field?.tr?.[i18n.language] ?? props.data?.field?.tr?.['en'] ?? ''

  const updateManager = () => {
    if (!props.data) {
      return
    }

    const { order, field } = props.data

    const model: OrderInternalInputModel | OrderSuppliersInputModel = {
      form: {
        ...order.form,
        fields: [
          { name: field.name, value: selectedManager, documents: [] },
          { name: 'AssignedAt', value: printDate(new Date(), true), documents: [] },
          { name: 'AssignedBy', value: user?.displayName, documents: [] },
        ],
      },
    }

    if (isSupplierOrder(order)) {
      if (model.form.status === WorkflowOrderStepStatus.New) {
        model.form.status = WorkflowOrderStepStatus.InProgress
      }
      updateOrderSuppliers({ id: order.id, model })
    } else {
      updateOrderInternal({ id: order.id, model })
    }
    props.onClose()
  }

  return (
    <Dialog open={!!props.data} title={title} onClose={props.onClose} icon="icon-edit" width={450}>
      <div className="manager-modal">
        <div className="selectArea">
          <select value={selectedManager} onChange={(e) => setSelectedManager(e.target.value)}>
            <option key={''} value={''} disabled></option>
            {managers.map((m) => (
              <option key={m.email} value={m.displayName}>
                {m.displayName}
              </option>
            ))}
          </select>
          <i className="icon-bottom" />
        </div>
        <div className="manager-modal-actions">
          <TransparentButton type={ButtonType.Negative} icon="icon-close" onClick={props.onClose} name={t('CANCEL')} />
          <TransparentButton type={ButtonType.Positive} icon="icon-check" onClick={updateManager} name={t('UPDATE')} />
        </div>
      </div>
    </Dialog>
  )
}
